import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LeapV2EvidenceService, StoredEvidenceFile } from '../../../tutor-report-flows/tutor-report-leap-v2/services/leap-v2-evidence.service';
import { BaseLearningSectionComponent } from '../../../../shared/learning-section/base-learning-section/base-learning-section.component';
import { LearningSectionFlowControlHelperService } from '../../../../_helpers/learning-section-flow-control-helper/learning-section-flow-control-helper.service';
import { LeapV2Ilp } from '../../../tutor-report-flows/tutor-report-leap-v2/models/leap-v2-ilp.model';
import { LeapV2IlpService } from '../../../tutor-report-flows/tutor-report-leap-v2/services/leap-v2-ilp.service';
import { SEMH_LABELS, SEMH_MIN, SEMH_MAX } from '../../../tutor-report-flows/tutor-report-leap-v2/models/leap-v2-semh';
import { KeyStage } from '../../../../_models/tutor-reports/tutorreport';
import { ilpStatusId, ilpStatusLabel } from '../../../tutor-report-flows/tutor-report-leap-v2/models/leap-v2-ilp-status';

/**
 * Timeline card for the LEAP v2 ILP. Replaces the legacy classic-ilp card: it fetches the current ILP
 * (GET api/Ilp/{placementId}/{studentId}) and renders the new shape read-only — academic score/target,
 * key stage, the five SEMH ratings, areas of strength/development, plan, evidence count and status —
 * with a click-through to the full ILP screen.
 */
@Component({
  selector: 'leap-v2-ilp-section',
  templateUrl: './leap-v2-ilp-section.component.html',
  styleUrls: ['./leap-v2-ilp-section.component.css'],
  standalone: false,
})
export class LeapV2IlpSectionComponent extends BaseLearningSectionComponent implements OnInit {
  ilp: LeapV2Ilp | null = null;
  loading = true;
  studentId: number;

  keyStages: KeyStage[] = [];
  readonly percentTicks = ['0%', '25%', '50%', '75%', '100%'];
  readonly semhLabels = SEMH_LABELS;
  readonly semhMin = SEMH_MIN;
  readonly semhMax = SEMH_MAX;

  constructor(
    protected readonly flowHelperService: LearningSectionFlowControlHelperService,
    public dialog: MatDialog,
    private ilpService: LeapV2IlpService,
    private router: Router,
    private evidenceService: LeapV2EvidenceService
  ) {
    super(flowHelperService, dialog);
    this.sectionTitle = 'Individual Learning Plan';
  }

  /** Opens an evidence file through the API (its link is api/Evidence/{id}, not a blob URL). */
  openEvidence(event: Event, file: StoredEvidenceFile): void {
    event.preventDefault();
    this.evidenceService.open(file);
  }

  ngOnInit(): void {
    this.studentId = this.students?.[0]?.studentId;
    if (!this.placementId || !this.studentId) {
      this.loading = false;
      return;
    }
    this.ilpService.loadIlp(this.placementId, this.studentId).subscribe(ilp => {
      this.ilp = ilp;
      this.loading = false;
    });
    this.ilpService.loadKeyStages(this.placementId, this.studentId)
      .subscribe(keyStages => this.keyStages = keyStages ?? []);
  }

  /** The stored initial key stage is a tblKeyStage Id; show its description. */
  keyStageLabel(): string {
    const id = this.ilp?.academic.initialKeyStage;
    return this.keyStages.find(k => k.id === id)?.description ?? '';
  }

  statusLabel(): string {
    return ilpStatusLabel(this.ilp?.statusId, this.ilp?.approved, 'Saved');
  }

  statusClass(): string {
    switch (ilpStatusId(this.ilp?.statusId, this.ilp?.approved)) {
      case 1: return 'pr-status--saved';
      case 2: return 'pr-status--submitted';
      case 3: return 'pr-status--approved';
      case 4: return 'pr-status--rejected';
      default: return '';
    }
  }

  openIlp(): void {
    this.router.navigate(['/individual-learning-plan', this.placementId, this.studentId]);
  }
}
